import { useCallback, useContext, useEffect, useRef } from 'react';
import useGetClientId from '~/constants/srp/getClientId';
import AppliedFiltersContext from '~/context/common/AppliedFiltersContext';
import DeviceContext, { useIsMobile } from '~/context/common/DeviceContext';
import { useReferrer } from '~/context/common/StaticContext';
import { useSearchRequestId } from '~/context/srp/InventoryContext';
import SearchContext from '~/context/srp/SearchContext';
import StaticContext from '~/context/srp/StaticContext';
import type { InventoryRequestAnalytics } from '~/contracts/common';
import getAnalyticsUtmParams from '~/server-props/srp/utils/getAnalyticsUtmParams';
import Cookies from '~/utilities/BrowserCookies';
import { getSearchSessionIdCookie } from '~/utilities/common/searchSessionIdCookie';

export default function useGenerateAnalyticsData(): () => InventoryRequestAnalytics {
  const { appliedFilters } = useContext(AppliedFiltersContext);
  const { browserName, deviceName, isBot } = useContext(DeviceContext);
  const { searchSessionId, utmParams } = useContext(SearchContext);
  const { sessionId } = useContext(StaticContext);
  const referrer = useReferrer();
  const searchRequestId = useSearchRequestId();
  const clientId = useGetClientId();
  const isMobile = useIsMobile();

  const lastActiveSession = Cookies.get('LastActiveSearchSession') || null;
  const hasFilters = Object.keys(appliedFilters).length > 0;
  const hasActiveSession = lastActiveSession === sessionId;
  const shouldUpdateLastActiveSession = lastActiveSession !== sessionId;

  // Use refs here because we don't want to trigger new API requests when
  // analytics data changes.
  const isFirstActiveSearchSession = useRef(
    (!hasActiveSession || shouldUpdateLastActiveSession) && hasFilters
  );
  useEffect(() => {
    isFirstActiveSearchSession.current =
      (!hasActiveSession || shouldUpdateLastActiveSession) && hasFilters;
  }, [hasActiveSession, hasFilters, shouldUpdateLastActiveSession]);

  const isMobileRef = useRef(isMobile);
  useEffect(() => {
    isMobileRef.current = isMobile;
  }, [isMobile]);

  const previousSearchRequestIdRef = useRef(searchRequestId);
  useEffect(() => {
    previousSearchRequestIdRef.current = searchRequestId;
  }, [searchRequestId]);

  return useCallback<() => InventoryRequestAnalytics>(() => {
    // Read at request time so a reset made earlier in the same commit is not missed.
    const cookieSearchSessionId = getSearchSessionIdCookie();
    const requestSearchSessionId = cookieSearchSessionId ?? searchSessionId;

    return {
      browser: browserName,
      clientId,
      deviceName,
      isBot,
      isFirstActiveSearchSession: isFirstActiveSearchSession.current,
      isMobileDevice: isMobileRef.current,
      previousSearchRequestId: previousSearchRequestIdRef.current,
      referrer,
      searchSessionId: requestSearchSessionId,
      utmParams: getAnalyticsUtmParams(utmParams)
    };
  }, [
    browserName,
    clientId,
    deviceName,
    isBot,
    referrer,
    searchSessionId,
    utmParams
  ]);
}
